import { DEFAULT_COMPETITIVE_CONFIG, type CompetitiveConfig } from "./config";
import {
  add,
  fromAngle,
  normalizeAngle,
  rotateTowards,
  scale,
  vec,
  type Vec2,
} from "./math";
import { SeededRng } from "./rng";
import { createTrail } from "./trail";
import { snakeWidthForLength } from "./tuning";
import type {
  DeepReadonly,
  DeathRecord,
  Food,
  MatchSnapshot,
  ReadonlyMatchSnapshot,
  SnakeController,
  SnakeIntent,
  SnakeState,
} from "./types";

export const byId = <Value extends { readonly id: string }>(
  left: Value,
  right: Value,
): number => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0);

export const cloneSnake = (snake: DeepReadonly<SnakeState>): SnakeState => ({
  ...snake,
  position: { ...snake.position },
  trail: { points: snake.trail.points.map((point) => ({ ...point })) },
});

export const cloneFood = (food: DeepReadonly<Food>): Food => ({
  ...food,
  position: { ...food.position },
});

export const cloneDeath = (death: DeepReadonly<DeathRecord>): DeathRecord => ({
  ...death,
  position: { ...death.position },
});

export const cloneSnapshot = (
  snapshot: ReadonlyMatchSnapshot,
): MatchSnapshot => ({
  ...snapshot,
  snakes: snapshot.snakes.map(cloneSnake),
  foods: snapshot.foods.map(cloneFood),
  deaths: snapshot.deaths.map(cloneDeath),
  rankings: snapshot.rankings.map((ranking) => ({ ...ranking })),
});

export interface SnakeCreation {
  readonly id: string;
  readonly controller?: SnakeController;
  readonly position?: Vec2;
  readonly heading?: number;
  readonly length?: number;
  readonly width?: number;
}

export const createSnake = ({
  id,
  controller = "human",
  position = vec(0, 0),
  heading = 0,
  length = 160,
  width = snakeWidthForLength(length),
}: SnakeCreation): SnakeState => ({
  id,
  controller,
  position,
  heading: normalizeAngle(heading),
  targetHeading: normalizeAngle(heading),
  length,
  width,
  speed: DEFAULT_COMPETITIVE_CONFIG.baseSpeed,
  alive: true,
  boosting: false,
  boostFragmentCarry: 0,
  trail: createTrail(position),
});

export const turnRateForLength = (
  length: number,
  config: CompetitiveConfig,
  speed = config.baseSpeed,
): number => {
  const safeSpeed =
    Number.isFinite(speed) && speed > 0 ? speed : config.baseSpeed;
  const minimumRadius = snakeWidthForLength(length);
  return safeSpeed / minimumRadius;
};

export const advanceSnakeKinematics = (
  snake: SnakeState,
  intent: SnakeIntent,
  config: CompetitiveConfig,
  stepSeconds: number,
): void => {
  snake.width = snakeWidthForLength(snake.length);
  const canBoost = intent.boosting && snake.length > config.minimumBoostLength;
  snake.boosting = canBoost;
  snake.speed = config.baseSpeed * (canBoost ? config.boostSpeedMultiplier : 1);
  snake.targetHeading = normalizeAngle(intent.targetHeading);
  snake.heading = rotateTowards(
    snake.heading,
    snake.targetHeading,
    turnRateForLength(snake.length, config, snake.speed) * stepSeconds,
  );
  snake.position = add(
    snake.position,
    scale(fromAngle(snake.heading), snake.speed * stepSeconds),
  );
};

export const foodValueAt = (
  food: Food,
  elapsedSeconds: number,
  config: CompetitiveConfig,
): number =>
  food.baseValue *
  (food.source === "natural" && food.resonantUntil > elapsedSeconds
    ? config.resonanceValueMultiplier
    : 1);

export interface DefaultMatchEntities {
  readonly snakes: readonly SnakeState[];
  readonly foods: readonly Food[];
}

export const createDefaultMatchEntities = (
  seed: string | number,
  config: CompetitiveConfig,
): DefaultMatchEntities => {
  const random = new SeededRng(seed);
  const snakes: SnakeState[] = [
    createSnake({
      id: "player",
      controller: "human",
      position: vec(0, 0),
      heading: 0,
    }),
  ];
  for (let index = 0; index < config.botCount; index += 1) {
    const angle = (index / config.botCount) * Math.PI * 2;
    snakes.push(
      createSnake({
        id: `bot-${String(index + 1).padStart(2, "0")}`,
        controller: "bot",
        position: scale(fromAngle(angle), 760),
        heading: normalizeAngle(angle + Math.PI / 2),
        length: 160,
      }),
    );
  }

  const foods: Food[] = [];
  const starterFoodCount = 6;
  for (let index = 0; index < config.naturalFoodCount; index += 1) {
    if (index < starterFoodCount) {
      foods.push({
        id: `natural-${String(index + 1).padStart(4, "0")}`,
        position: vec(55 + index * 38, index % 2 === 0 ? -3 : 3),
        radius: 3,
        baseValue: 1,
        source: "natural",
        resonantUntil: 0,
      });
      continue;
    }
    const angle = random.between(-Math.PI, Math.PI);
    const radius = Math.sqrt(random.next()) * (config.arenaRadius - 50);
    foods.push({
      id: `natural-${String(index + 1).padStart(4, "0")}`,
      position: scale(fromAngle(angle), radius),
      radius: 3,
      baseValue: random.next() < 0.12 ? 2 : 1,
      source: "natural",
      resonantUntil: 0,
    });
  }
  return { snakes, foods };
};
